import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { addonServices, serviceOfferings } from '@/lib/marketing';

const PricingSection = () => {
  return (
    <section id="services" className="mx-auto mt-32 w-full max-w-7xl px-4 text-left">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="mx-auto max-w-3xl text-center"
      >
        <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
          Enterprise Sovereign AI services
        </Badge>
        <h2 className="mt-4 text-4xl font-semibold tracking-tight md:text-5xl">
          Enterprise AI services for secure deployment, customization, and long-term ownership.
        </h2>
        <p className="mt-5 text-lg leading-8 text-muted-foreground">
          CanXP AI helps organizations deploy, adapt, and operate private AI systems with confidence. From scoped service packages to full enterprise programs, we provide the architecture, implementation, and deployment planning needed to build sovereign AI capability.
        </p>
      </motion.div>

      <div className="mx-auto mt-14 flex max-w-[1320px] flex-wrap justify-center gap-6">
        {serviceOfferings.map((service, index) => (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.08 * index }}
            className="h-full w-full md:w-[calc(50%-0.75rem)] xl:w-[calc(33.333%-1rem)] xl:max-w-[410px]"
          >
            <Card
              className={[
                'flex h-full flex-col border-border/70 bg-background/85 shadow-[0_16px_60px_-36px_rgba(220,38,38,0.5)] backdrop-blur',
                service.featured ? 'border-primary/35 bg-gradient-to-b from-primary/10 via-background to-background' : '',
              ].join(' ')}
            >
              <CardHeader className="space-y-4 pb-5">
                <div className="flex items-center justify-between gap-3">
                  <Badge variant={service.ctaType === 'purchase' ? 'default' : 'secondary'} className="w-fit">
                    {service.label}
                  </Badge>
                  {service.featured ? (
                    <span className="text-xs font-medium uppercase tracking-[0.22em] text-primary">Priority</span>
                  ) : null}
                </div>
                <div>
                  <CardTitle className="text-2xl leading-tight">{service.name}</CardTitle>
                  <div className="mt-4 space-y-1">
                    <p className="text-2xl font-semibold tracking-tight text-primary">{service.price}</p>
                    {service.priceDetail ? (
                      <p className="text-sm font-medium text-foreground/80">{service.priceDetail}</p>
                    ) : null}
                  </div>
                </div>
                <p className="text-sm leading-6 text-muted-foreground">{service.description}</p>
              </CardHeader>

              <CardContent className="flex-1 space-y-6">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">In scope</p>
                  <ul className="mt-3 space-y-3">
                    {service.inclusions.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm leading-6 text-foreground/90">
                        <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Best for</p>
                  <p className="mt-2 text-sm leading-6 text-foreground/90">{service.bestFor}</p>
                </div>
              </CardContent>

              <CardFooter className="pt-6">
                <Button asChild className="w-full gap-2" variant={service.ctaType === 'purchase' ? 'default' : 'outline'}>
                  <Link to={service.href}>
                    {service.ctaLabel}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.25 }}
        className="mt-24 rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-background p-8 md:p-10"
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <Badge variant="outline" className="border-primary/25 bg-background/60 text-primary">
              Enterprise Add-on services
            </Badge>
            <h3 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
              Add specialized infrastructure, testing, and deployment workstreams as needed.
            </h3>
            <p className="mt-4 text-base leading-7 text-muted-foreground">
              These add-ons are commonly paired with sovereign adaptation, hosting, and deployment programs when teams
              need deeper data engineering, private infrastructure, or implementation support.
            </p>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {addonServices.map((addon) => (
            <Card key={addon.name} className="border-border/60 bg-background/80 shadow-none">
              <CardContent className="flex h-full items-start gap-4 p-5">
                <div className="rounded-full border border-primary/25 bg-primary/10 p-2 text-primary">
                  <Plus className="h-4 w-4" />
                </div>
                <div className="space-y-2">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <h4 className="text-base font-semibold text-foreground">{addon.name}</h4>
                    <span className="text-sm font-semibold text-primary">{addon.price}</span>
                  </div>
                  <p className="text-sm leading-6 text-muted-foreground">{addon.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default PricingSection;